/**
 * Compression des photos de cartes AVANT envoi.
 * Une photo d'iPhone fait 3-8 Mo : l'envoyer brute tuerait la latence (porte 2)
 * et gonflerait le coût vision. On réduit à ~1600 px de côté max, ~300 Ko.
 */

const COTE_MAX = 1600;
const QUALITE_JPEG = 0.82;

/** Calcule les dimensions cibles en préservant le ratio, plus grand côté plafonné. */
export function calculerDimensions(
  largeur: number,
  hauteur: number,
  coteMax = COTE_MAX,
): { largeur: number; hauteur: number } {
  const plusGrand = Math.max(largeur, hauteur);
  if (plusGrand <= coteMax) return { largeur, hauteur };
  const facteur = coteMax / plusGrand;
  return {
    largeur: Math.round(largeur * facteur),
    hauteur: Math.round(hauteur * facteur),
  };
}

/**
 * Décode une image en bitmap. Repli HEIC/HEIF (photos iPhone) : ces formats ne
 * sont pas décodables nativement par tous les navigateurs (Chrome desktop
 * notamment) ; on convertit alors en JPEG via heic2any, chargé À LA DEMANDE
 * (lib lourde, hors du bundle principal).
 */
/** Taille max d'un fichier converti par heic2any (WASM sur le thread principal). */
const TAILLE_MAX_HEIC = 25 * 1024 * 1024;

async function decoderImage(fichier: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(fichier);
  } catch {
    // Format non décodable nativement (souvent HEIC/HEIF). heic2any tourne sur le
    // thread principal : on borne la taille pour ne pas geler l'UI sur un énorme
    // fichier (ou un fichier qui n'est pas une image du tout).
    if (fichier.size > TAILLE_MAX_HEIC) {
      throw new Error('Image trop lourde pour la conversion (max 25 Mo).');
    }
    const { default: heic2any } = await import('heic2any');
    const jpeg = (await heic2any({
      blob: fichier,
      toType: 'image/jpeg',
      quality: 0.85,
    })) as Blob;
    return await createImageBitmap(jpeg);
  }
}

/** Compresse un fichier image en JPEG réduit (navigateur uniquement). */
export async function compresserImage(fichier: Blob): Promise<Blob> {
  const bitmap = await decoderImage(fichier);
  const { largeur, hauteur } = calculerDimensions(bitmap.width, bitmap.height);

  const canvas = document.createElement('canvas');
  canvas.width = largeur;
  canvas.height = hauteur;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close(); // libère le bitmap même sur le chemin d'erreur (anti-fuite)
    throw new Error('Canvas indisponible');
  }
  ctx.drawImage(bitmap, 0, 0, largeur, hauteur);
  bitmap.close();

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Échec de compression'))),
      'image/jpeg',
      QUALITE_JPEG,
    );
  });
}
